import {
  useEffect,
  useRef,
} from 'react';

import { CheckMark } from '@/core/components/CheckMark';

import {
  CheckMarkWrapper,
  DonateLink,
  DonateLinkWrapper,
  ShareIconsWrapper,
  ThankYouHeadline,
  WantMoreText,
  Wrapper,
} from './ThankYouWrapper.styles';
import { ShareIcons } from '../ShareIcons';

interface ThankYouWrapperProps {
  borderColor: string;
  buttonColor: string;
  children: React.ReactNode;
  isDonateButton: boolean;
  isFullScreen?: boolean;
  linkDonate: string | null;
  name: string;
  socialShareButtonEnabled: boolean;
  textcolor: string;
  uuid?: string | Array<string>;
  shouldFocus?: boolean;
}

export const ThankYouWrapper = ({
  borderColor,
  buttonColor,
  children,
  isDonateButton,
  isFullScreen,
  linkDonate,
  name,
  socialShareButtonEnabled,
  textcolor,
  uuid,
  shouldFocus = false,
}: ThankYouWrapperProps) => {
  const headlineRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (shouldFocus && headlineRef.current) {
      // Scroll the heading into view
      if (headlineRef.current.scrollIntoView) {
        headlineRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }

      // Programmatically set focus to the heading for screen readers
      if (headlineRef.current.focus) {
        headlineRef.current.focus();
      }
    }
  }, [shouldFocus]);

  return (
    <Wrapper
      $borderColor={borderColor}
      $isFullScreen={isFullScreen}
    >
      <CheckMarkWrapper>
        <CheckMark backgroundColor={textcolor} />
      </CheckMarkWrapper>
      <ThankYouHeadline
        ref={headlineRef}
        tabIndex={-1}
      >
        {name}
      </ThankYouHeadline>
      {children}
      {isDonateButton && linkDonate && (
        <DonateLinkWrapper>
          <DonateLink
            backgroundcolor={buttonColor}
            href={linkDonate.startsWith('http://') || linkDonate.startsWith('https://') ? linkDonate : `https://${linkDonate}`}
            rel="noreferrer"
            textcolor={textcolor}
            target="_blank"
          >
            Donate
          </DonateLink>
        </DonateLinkWrapper>
      )}
      {socialShareButtonEnabled && typeof uuid === 'string' && (
        <>
          <WantMoreText>Take one more step and share with your friends</WantMoreText>
          <ShareIconsWrapper>
            <ShareIcons
              textcolor={buttonColor}
              customIcons
              uuid={uuid}
            />
          </ShareIconsWrapper>
        </>
      )}
    </Wrapper>
  );
};

ThankYouWrapper.defaultProps = {
  uuid: null,
};
